// src/components/ShopList.js - Version avec validation automatique
import React, { useState } from 'react';
import { Globe, Phone, MapPin, Edit2, ExternalLink, Trash2 } from 'lucide-react';
import { validateShop } from '../utils/shopValidation';

const ShopList = ({ stats, shops, selectedShop, onSelectShop, onEditShop, onDeleteShop, hasEditPermission, hasDeletePermission, loading, statusConfig }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const handleEditClick = (e, shop) => {
    e.stopPropagation();
    if (onEditShop) {
      onEditShop(shop);
    }
  };

  const handleViewClick = (e, shop) => {
    e.stopPropagation();
    const url = shop.domain.startsWith('http') ? shop.domain : `https://${shop.domain}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDeleteClick = async (e, shop) => {
    e.stopPropagation();
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${shop.name || shop.domain}" ?\n\nCette action marquera la boutique comme rejetée.`)) {
      return;
    }
    if (onDeleteShop) {
      await onDeleteShop(shop._id);
    }
  };

  // Pagination
  const totalPages = Math.ceil(stats[shops[0]?.status] / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentShops = shops.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    // Scroll to top of list
    document.querySelector('.shops-list')?.scrollTo(0, 0);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    // First page
    if (startPage > 1) {
      pages.push(
        <button key="1" onClick={() => goToPage(1)} className="page-btn">
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="dots1" style={{ padding: '0 8px' }}>...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => goToPage(i)}
          className={`page-btn ${currentPage === i ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="dots2" style={{ padding: '0 8px' }}>...</span>);
      }
      pages.push(
        <button key={totalPages} onClick={() => goToPage(totalPages)} className="page-btn">
          {totalPages}
        </button>
      );
    }

    return (
      <div className="pagination" style={{ 
        padding: '16px 20px', 
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="page-btn"
          style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
        >
          ← Précédent
        </button>
        
        <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
          {pages}
        </div>

        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="page-btn"
          style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
        >
          Suivant →
        </button>
      </div>
    );
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2>
          Boutiques ({shops.length})
          {totalPages > 1 && (
            <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: 'normal', marginLeft: '8px' }}>
              - Page {currentPage}/{totalPages}
            </span>
          )}
        </h2>
        {loading && <span className="loading-text">Chargement...</span>}
      </div>
      <div className="shops-list">
        {currentShops.map(shop => {
          // Combiner les erreurs du shop avec les erreurs de validation
          const validationErrors = validateShop(shop);
          const allErrors = [...(shop.errors || []), ...validationErrors];
          
          return (
            <div
              key={shop._id}
              onClick={() => onSelectShop(shop)}
              className={`shop-item ${selectedShop?._id === shop._id ? 'active' : ''}`}
            >
              <div className="shop-item-content">
                {/* Logo */}
                <div className="shop-logo">
                  {shop.logo ? (
                    <img src={shop.logo} alt={shop.name || shop.domain} onError={(e) => e.target.style.display = 'none'} />
                  ) : (
                    <div className="shop-logo-placeholder">
                      <Globe className="placeholder-icon" />
                    </div>
                  )}
                </div>

                <div className="shop-info">
                  <div className="shop-header">
                    <h3>{shop.name || shop.domain}</h3>
                  </div>
                  {shop.category && (
                    <p className="shop-category">{shop.category}</p>
                  )}
                  <div className="shop-stats">
                    <span className="shop-stat">
                      <Phone className="small-icon" />
                      {shop.phones?.length || 0} tél.
                    </span>
                    <span className="shop-stat">
                      <MapPin className="small-icon" />
                      {shop.addresses?.length || 0} adr.
                    </span>
                  </div>
                  {allErrors.length > 0 && (
                    <div className="errors">
                      {allErrors.slice(0, 3).map((error, idx) => (
                        <span key={idx} className="error-badge" style={{ display: 'block', marginBottom: '4px' }}>
                          {error}
                        </span>
                      ))}
                      {allErrors.length > 3 && (
                        <span className="error-badge" style={{ display: 'block' }}>
                          +{allErrors.length - 3} autres erreurs
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Boutons d'action */}
                <div style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  alignItems: 'center',
                  marginLeft: 'auto'
                }}>
                  {/* Bouton voir le site */}
                  <button
                    onClick={(e) => handleViewClick(e, shop)}
                    className="shop-edit-btn"
                    aria-label="Voir le site"
                    title="Voir le site web"
                    style={{ backgroundColor: '#eff6ff', borderColor: '#3b82f6' }}
                  >
                    <ExternalLink size={16} style={{ color: '#3b82f6' }} />
                  </button>

                  {/* Bouton d'édition */}
                  {hasEditPermission && (
                    <button
                      onClick={(e) => handleEditClick(e, shop)}
                      className="shop-edit-btn"
                      aria-label="Éditer la boutique"
                      title="Éditer cette boutique"
                    >
                      <Edit2 size={16} />
                    </button>
                  )}

                  {/* Bouton de suppression */}
                  {hasDeletePermission && shop.status !== 'rejected' && (
                    <button
                      onClick={(e) => handleDeleteClick(e, shop)}
                      className="shop-edit-btn"
                      aria-label="Supprimer la boutique"
                      title="Marquer comme rejetée"
                      style={{ backgroundColor: '#fee2e2', borderColor: '#ef4444' }}
                    >
                      <Trash2 size={16} style={{ color: '#ef4444' }} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {shops.length === 0 && !loading && (
          <div className="empty-state">
            Aucune boutique trouvée
          </div>
        )}
      </div>
      {renderPagination()}
    </div>
  );
};

export default ShopList;